import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { ConversationEventMessages, getRenderableEventMessages } from '../ConversationEventMessages';
import { styleSheet } from './styles';

interface IProps {
	conversationEvent: Api.IConversationThreadEvent;
	entity?: Api.IEntity | Api.TimelineEventViewModel<Api.ITimelineEvent>;
	isOpen: boolean;
	onRequestClose: () => void;
	onViewFullConversationClicked: () => void;
}

const formatSentDateRange = (messages: Api.ITextMessage[], eventTimestamp?: string) => {
	const sentDates = messages
		.map(message => message.sentDate ?? message.creationDate)
		.filter(Boolean)
		.map(date => moment(date))
		.filter(date => date.isValid())
		.sort((a, b) => a.valueOf() - b.valueOf());

	const first = sentDates[0] ?? (eventTimestamp ? moment(eventTimestamp) : null);
	if (!first?.isValid()) {
		return null;
	}

	const last = sentDates[sentDates.length - 1] ?? first;
	return first.isSame(last, 'day')
		? first.format('MMMM D, YYYY')
		: `${first.format('MMMM D, YYYY')} – ${last.format('MMMM D, YYYY')}`;
};

export const ConversationEventMessagesModal = ({
	conversationEvent,
	entity,
	isOpen,
	onRequestClose,
	onViewFullConversationClicked,
}: IProps) => {
	const messages = getRenderableEventMessages(conversationEvent?.lastMessages);
	const sentDateRange = formatSentDateRange(messages, conversationEvent?.timestamp);

	return (
		<Dialog
			open={isOpen}
			onOpenChange={open => {
				if (!open) {
					onRequestClose();
				}
			}}
		>
			<DialogPopup styles={[styleSheet.dialogPopup]}>
				<DialogHeader>
					<DialogTitle>{conversationEvent?.title || 'Text Message'}</DialogTitle>
					{!!sentDateRange && <DialogDescription>{sentDateRange}</DialogDescription>}
				</DialogHeader>
				<DialogBody styles={[styleSheet.messages]}>
					{messages.length ? (
						<ConversationEventMessages entity={entity} messages={messages} />
					) : (
						<div className={css(styleSheet.noMessages)}>No messages found</div>
					)}
				</DialogBody>
				<DialogFooter>
					<Button onClick={onViewFullConversationClicked} variant='secondary'>
						View full conversation
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
